<!-- eslint-disable vue/no-v-html -->
<template>
  <router-link :to="content.url" class="article-card">
    <component :is="titleTag" class="article-card__title">
      {{ content.title }}
    </component>
    <span v-if="content.subtitle" class="article-card__subtitle">
      {{ content.subtitle }}
    </span>
    <GPicture
      v-if="content.picture"
      :content="{ picture: content.picture }"
      sizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 350px"
      class="article-card__picture"
    />
    <p
      v-if="content.excerpt"
      class="article-card__text p-medium"
      v-html="content.excerpt"
    ></p>
    <ul v-if="content.categories" class="article-card__tags">
      <li
        v-for="tag in content.categories"
        :key="tag"
        class="article-card__tags__item tag p-medium"
      >
        {{ tag.label }}
      </li>
    </ul>
    <div class="article-card__footer">
      {{ t('read-article') }}
      <SvgSprite
        symbol="ui-full-arrow"
        size="23"
        class="article-card__footer__icon"
      />
    </div>
  </router-link>
</template>

<script setup lang="ts">
import { useChromeStore } from '@/stores/chrome'

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
  titleTag: {
    type: String,
    default: 'h2',
  },
})

const { t } = useChromeStore()
</script>

<style lang="scss" scoped>
.article-card {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 2rem;
  color: var(--c-foreground);
  text-decoration: none;
  border: 1px solid var(--c-foreground);
  border-radius: 6px;
  transition: all 0.24s ease-in-out;

  &:hover {
    color: var(--c-background);
    background-color: var(--c-foreground);

    .tag,
    .article-card__footer {
      border-color: var(--c-background);
    }

    .article-card__footer svg {
      fill: var(--c-background);
    }

    .article-card__picture .picture__img {
      transform: scale(1.1);
    }
  }

  @include mq(s) {
    .is-highlighted & {
      padding-left: 53%;
    }
  }

  @include mq(m) {
    padding: 3rem;
  }
}

.article-card__subtitle,
.article-card__title {
  display: block;
  margin: 0 0 2rem;
}

.article-card__title {
  // prettier-ignore
  @include fluid(font-size,(xxs: 30px, xxl: 35px));

  // prettier-ignore
  @include fluid(line-height,(xxs: 35px, xxl: 40px));
}

.article-card__subtitle {
  @include fluid(
    font-size,
    (
      xxs: 18px,
      xxl: 20px,
    )
  );
  @include fluid(
    line-height,
    (
      xxs: 28px,
      xxl: 25px,
    )
  );

  margin: -1rem 0 2rem;
  font-family: $ff-default;
  font-weight: 600;
}

.article-card__picture {
  position: relative;
  overflow: hidden;
  height: 23rem;
  margin-bottom: 2rem;
  border-radius: 4px;

  :deep(.picture) {
    height: 100%;
  }

  :deep(.picture__img) {
    @include image-fit;

    background-color: #dbeddd;

    img {
      height: 100%;
      object-fit: cover;
    }
  }

  @include mq(s) {
    .is-highlighted & {
      position: absolute;
      top: 3rem;
      left: 3rem;
      width: 44%;
      height: calc(100% - 6rem);
    }
  }
}

.article-card__text {
  display: -webkit-box;
  overflow: hidden;
  margin: 0 0 2rem;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  white-space: break-spaces;
}

.article-card__tags {
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 2rem;
  padding: 0;
}

.article-card__tags__item {
  padding: 0.2rem 0.8rem;
  font-size: 1.2rem;

  &:not(:last-child) {
    margin-right: 1rem;
  }
}

.article-card__footer {
  @extend %fw-bold;

  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 2rem;
  font-size: 1.4rem;
  line-height: 2.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-top: 1px solid var(--c-foreground);

  svg {
    margin-left: 1rem;
    fill: var(--c-foreground);
    transition: transform 0.15s;
  }

  .article-card:hover & {
    svg {
      transform: translateX(0.5rem);
    }
  }
}
</style>
